/**
 * noShowQuips.js — the bot's running commentary on repeat offenders.
 * 2026-09-22 (per Gilles: "come up with a funny way to harass them").
 *
 * Shown under a player's row when they check in to a game and have one or
 * more prior no-shows on this team. Beer-league ribbing, not cruelty — the
 * point is that the whole room sees it and the offender laughs first.
 *
 * Deterministic: the same player on the same game always gets the same
 * line, so it does not flicker between renders or change every time
 * someone refreshes. Escalates with the count.
 */

const TIER_1 = [
  'Are you *really* coming this time? 🤔',
  'Checked in! Just like last time. We\'ll see.',
  'Bold of you to check in again. Respect.',
  'The bench still has your name on the spot you didn\'t fill.',
  'Last time you said "in", we played a man short. No pressure.',
  'The goalie has been told to expect you. He laughed.',
  'Checked in. The team remains cautiously optimistic.',
];

const TIER_2 = [
  'Two no-shows. The Zamboni is more reliable than you.',
  'Fool me once, shame on you. Fool me twice — we\'re keeping score now.',
  'Checked in. The team will believe it when skates hit ice.',
  'Your jersey has been reported missing. Along with you. Twice.',
  'Two strikes. The spares list has been quietly notified.',
  'Checked in again. Somewhere, a spare is warming up just in case.',
];

const TIER_3 = [
  'Legend says you once played hockey. The team has found no evidence.',
  'Checked in. Historically, this has meant nothing.',
  'At this point you\'re less a teammate and more a rumour.',
  'There\'s a pool going on whether you show. The odds are unkind.',
  'Checked in. The goalie has stopped believing in things.',
  'Your seat on the bench has been reclassified as decorative.',
];

/** Small stable hash so the pick is deterministic per (user, game). */
function hash(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * @param {number} priorNoShows  count of no-shows on this team BEFORE this game
 * @param {string} seed          something stable per row, e.g. `${userId}:${gameId}`
 * @returns {string|null}        a quip, or null when there is nothing to rib
 */
export function noShowQuip(priorNoShows, seed) {
  const n = Number(priorNoShows) || 0;
  if (n <= 0) return null;
  const pool = n >= 3 ? TIER_3 : n === 2 ? TIER_2 : TIER_1;
  const line = pool[hash(String(seed)) % pool.length];
  // Let the worst offenders see their number.
  return n >= 3 ? line.replace(/^/, `${n} no-shows. `) : line;
}

/** Badge label: 1 → "UNRELIABLE", 2+ → "UNRELIABLE ×2". */
export function noShowBadgeLabel(priorNoShows) {
  const n = Number(priorNoShows) || 0;
  if (n <= 0) return null;
  return n === 1 ? 'UNRELIABLE' : `UNRELIABLE ×${n}`;
}
